import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import RoslinBranding from '../components/RoslinBranding';
import '../styles/HomePage.css';

interface Quiz {
  id: number;
  title: string;
  description: string;
}

const HomePage: React.FC = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isGuestAccess, isSupervisor, isAdmin, user } = useAuth();

  useEffect(() => {
    const fetchQuizzes = async () => {
      // Only fetch quizzes if user has access (authenticated, admin, supervisor, or guest)
      // This prevents 401 errors when logged out
      if (!isGuestAccess && !isSupervisor && !isAdmin && !user) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await api.get('/api/quizzes');
        setQuizzes(response.data);
        setLoading(false);
      } catch (err: any) {
        // Only log errors if not a 401 (401 is expected when not authenticated)
        if (err.response?.status !== 401) {
          console.error('Error fetching quizzes:', err);
          setError(err.message || 'Failed to fetch quizzes');
        }
        setLoading(false);
      }
    };
    
    fetchQuizzes();
  }, [isGuestAccess, isSupervisor, isAdmin, user]);

  return (
          <div className="homepage">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="container">
            <RoslinBranding variant="large" className="hero-branding" showTagline={true} />
            <p className="hero-description">
              Roslin's in-house assessment platform for staff training and recruitment<br/>
              Dispatch workflows, Customer care, and English level checks.
            </p>
          
          {isGuestAccess && (
            <div className="guest-notice-banner">
              <p className="guest-notice-text">
                Welcome! You have been granted temporary access to take quizzes for your interview.
              </p>
              <div className="guest-info">
                <p>Your access is temporary and will expire automatically.</p>
                <p>Please complete your assigned quiz within the time limit.</p>
              </div>
            </div>
          )}
          
          {isSupervisor && (
            <div className="supervisor-notice-banner">
              <p className="supervisor-notice-text">
                Welcome Supervisor!
              </p>
              <p>You have access to review and rate quiz responses for your assigned quizzes.</p>
              <Link to="/admin" className="btn btn-primary btn-large">
                Go to Dashboard →
              </Link>
            </div>
          )}
          
          {user && user.role === 'user' && (
            <div className="user-notice-banner">
              <p className="user-notice-text">
                Welcome {user.name}!
              </p>
              <p>You can access quizzes through direct links provided by your supervisor or administrator.</p>
              <p>If you have a quiz link, please use it to access your assigned quiz.</p>
            </div>
          )}
        </div>
      </div>

      {/* Available Quizzes Section - Only show for users with quiz access */}
      {(isAdmin || isSupervisor || isGuestAccess) && (
        <div className="quizzes-section">
          <div className="container">
            <h2>Available Quizzes</h2>  
            
            {error && (
              <div className="alert alert-error">
                <p>{error}</p>
              </div>
            )}
            
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading quizzes...</p>
              </div>
            ) : quizzes.length === 0 ? (
              <div className="empty-state">
                <p>No quizzes available at the moment. Please check back later.</p>
              </div>
            ) : (
              <div className="quiz-grid">
                {quizzes.map(quiz => (
                  <div className="quiz-card" key={quiz.id}>
                    <div className="quiz-card-content">
                      <h3 className="quiz-card-title">{quiz.title}</h3>
                      <p className="quiz-card-description">{quiz.description}</p>
                      <Link to={`/quiz/${quiz.id}`} className="quiz-card-button">
                        Take Quiz
                        <div className="arrow-icon">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path d="M2.5 6H9.5M9.5 6L6.5 3M9.5 6L6.5 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;